import { useEffect, useState } from "react";

import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";

import type { HostTextSlot, SiteContent } from "../../../api/types";
import {
  groupTextIsAuto,
  groupTextValue,
  groupTexts,
  HOST_TEXT_MAX,
  setGroupText,
  type TextGroup,
} from "../../../model/hostExtras";
import { pageCn } from "./hostLabels";

function GroupRow({
  group,
  value,
  auto,
  disabled,
  onCommit,
}: {
  group: TextGroup;
  value: string;
  /** 出稿时 AI 按客户业务改写的(没被用户改过)。 */
  auto: boolean;
  disabled?: boolean;
  onCommit: (v: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const changed = value !== group.defaultValue;
  const where =
    group.pages.length > 1 ? `${group.pages.length} 个页面` : pageCn(group.pages[0] ?? "");
  const label = `${group.role === "nav" ? "导航" : "按钮"}「${group.defaultValue}」`;
  return (
    <li
      role="group"
      aria-label={label}
      data-changed={changed ? "1" : "0"}
      className="flex items-center gap-2"
    >
      <span
        className="w-28 shrink-0 truncate text-xs text-muted-foreground"
        title={group.defaultValue}
      >
        {group.defaultValue}
      </span>
      <Input
        aria-label={label}
        className="h-7 flex-1 text-xs"
        value={text}
        maxLength={HOST_TEXT_MAX}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onCommit(text)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit(text);
        }}
      />
      <span className="w-20 shrink-0 text-right text-2xs text-muted-foreground">
        {changed ? (auto ? "AI 改写 · " : "已改 · ") : ""}
        {where}
      </span>
    </li>
  );
}

/**
 * 1009 编辑页「导航与按钮」:导航菜单文字 + 按钮 / 界面文字。同一句模板原文(各页导航栏里的「产品中心」)归成一行,改一次全站生效;
 * 改回原文或清空 = 撤销。★ 只有改过的才发到宿主,没改的照旧用模板原文;菜单链接不能改。
 * 1009 C 线:出稿时 AI 会把不贴客户业务的模板词改掉(`textsAuto`),行尾标「AI 改写」;用户一改就变「已改」。
 */
export function HostTextsPanel({
  content,
  slots,
  disabled,
  onChange,
}: {
  content: SiteContent;
  slots: readonly HostTextSlot[];
  disabled?: boolean;
  onChange: (update: (prev: SiteContent) => SiteContent) => void;
}) {
  const groups = groupTexts(slots);
  if (groups.length === 0) return null;
  const nav = groups.filter((g) => g.role === "nav");
  const ui = groups.filter((g) => g.role !== "nav");
  const render = (title: string, list: TextGroup[]) =>
    list.length === 0 ? null : (
      <div className="space-y-1.5">
        <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
        <ul className="space-y-1.5">
          {list.map((g) => (
            <GroupRow
              key={g.key}
              group={g}
              value={groupTextValue(content, g)}
              auto={groupTextIsAuto(content, g)}
              disabled={disabled}
              onCommit={(v) => onChange((prev) => setGroupText(prev, g, v))}
            />
          ))}
        </ul>
      </div>
    );
  return (
    <section
      aria-label="导航与按钮"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">导航与按钮</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          改过的才会换,没改的沿用模板原文;每条最多 {HOST_TEXT_MAX} 字。标「AI
          改写」的是生成初稿时按你的业务换掉的模板词(如「定价方案」「免费试用」),你改过的重新生成初稿也会保留。
        </p>
      </div>
      {render("导航菜单", nav)}
      {render("按钮与界面文字", ui)}
    </section>
  );
}
